import { Link } from 'react-router-dom'
import { PenLine, Mail, Heart, Shield } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { signInAnonymously } from 'firebase/auth'
import { auth } from '../services/firebase'
import { useState, useEffect, useRef } from 'react'
import { trackUserSession } from '../services/userTracking'
import { logAnonymousSignIn, logPageView, setAnalyticsUserId, logButtonClick } from '../services/analytics'

export default function HomePage() {
  const { user } = useAuth()
  const [isSigningIn, setIsSigningIn] = useState(false)
  const hasTrackedSession = useRef(false)

  // Log page view
  useEffect(() => {
    logPageView('home')
  }, [])

  // Track user session when they first authenticate
  useEffect(() => {
    if (user && !hasTrackedSession.current) {
      hasTrackedSession.current = true
      setAnalyticsUserId(user.uid)
      trackUserSession().catch(console.error)
    }
  }, [user])

  const handleAnonymousSignIn = async () => {
    if (user) return
    setIsSigningIn(true)
    logButtonClick('anonymous_sign_in', 'home')
    try {
      await signInAnonymously(auth)
      logAnonymousSignIn()
      // Session tracking will happen in useEffect when user state updates
    } catch (error) {
      console.error('Failed to sign in:', error)
    } finally {
      setIsSigningIn(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Hero Section */}
      <section className="text-center mb-16 animate-fade-in">
        <div className="inline-flex items-center bg-rose-100 text-rose-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
          <span>Sevgiler Günü'nü kutla!</span>
        </div>
        
        <div className="flex justify-center mb-6">
          <img 
            src="/logo.svg" 
            alt="Logo" 
            className="h-12 sm:h-14 lg:h-16 w-auto max-w-[66%]"
            style={{ objectFit: 'contain' }}
          />
        </div>
        
        <p className="font-body text-lg sm:text-xl text-ink/70 max-w-2xl mx-auto mb-8 leading-relaxed">
        İçinden gelen cümleleri yaz, başka birine anonim olarak gönder. Sonra dev bir mektup havuzundan kendi payına düşeni oku. Arkadaşlıklar anonim ama duygular ve insanlar çok gerçek!
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {user ? (
            <>
              <Link to="/write" className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center">
                <PenLine className="w-5 h-5" />
                Mektup Yaz
              </Link>
              <Link to="/read" className="btn-secondary flex items-center gap-2 w-full sm:w-auto justify-center">
                <Mail className="w-5 h-5" />
                Mektup Oku
              </Link>
            </>
          ) : (
            <button 
              onClick={handleAnonymousSignIn}
              disabled={isSigningIn}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              <Heart className="w-5 h-5" />
              {isSigningIn ? 'Giriş yapılıyor...' : 'Anonim Başla'}
            </button>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="grid sm:grid-cols-3 gap-6 mb-16">
        <FeatureCard
          icon={<PenLine className="w-6 h-6" />}
          title="Yaz"
          description="İçinden geçenleri yaz. Mektubun havuza eklenir ve başka birine ulaşmayı bekler."
          delay={0}
        />
        <FeatureCard
          icon={<Mail className="w-6 h-6" />}
          title="Oku"
          description="Havuzdan rastgele bir mektup al, tanımadığın biri belki de en yakın arkadaşın olur."
          delay={100}
        />
        <FeatureCard
          icon={<Shield className="w-6 h-6" />}
          title="Güvenli"
          description="Tüm mektuplar içerik kontrolünden geçer. Saygısız içerikler engellenir."
          delay={200}
        />
      </section>

      {/* How it works */}
      <section className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-rose-100">
        <h2 className="font-display text-2xl font-semibold text-ink text-center mb-8">
          Nasıl Çalışır?
        </h2>
        
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
          <Step number={1} text="Anonim giriş yap" />
          <Arrow />
          <Step number={2} text="Mektubunu yaz" />
          <Arrow />
          <Step number={3} text="Havuzdan mektup oku" />
        </div>

        <p className="text-center text-ink/60 font-body text-sm mt-8">
          💡 Her mektup sadece bir kez okunabilir ve senin yazdığın mektuplar sana gelmez.
        </p>
      </section>
    </div>
  )
}

interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  description: string
  delay: number
}

function FeatureCard({ icon, title, description, delay }: FeatureCardProps) {
  return (
    <div 
      className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-rose-100 
                 hover:border-rose-200 hover:shadow-lg hover:shadow-rose-500/10 
                 transition-all duration-300 animate-slide-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="w-12 h-12 bg-gradient-to-br from-rose-100 to-rose-200 rounded-lg 
                      flex items-center justify-center text-rose-600 mb-4">
        {icon}
      </div>
      <h3 className="font-display text-lg font-semibold text-ink mb-2">{title}</h3>
      <p className="font-body text-ink/70 text-sm leading-relaxed">{description}</p>
    </div>
  )
}

interface StepProps {
  number: number
  text: string
}

function Step({ number, text }: StepProps) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-rose-600 rounded-full 
                      flex items-center justify-center text-white font-display font-bold shadow-lg shadow-rose-500/30">
        {number}
      </div>
      <span className="font-body text-ink">{text}</span>
    </div>
  )
}

function Arrow() {
  return (
    <div className="hidden md:block text-rose-300">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </div>
  )
}
